import { Link } from 'react-router-dom'
import { Fragment, useMemo, type ReactNode } from 'react'
import { ChevronsUpDown } from 'lucide-react'
import { cn } from '@lib/utils/helpers'
import { AccessConfirmationIconButton } from '@/components/ui/AccessConfirmationIconButton'
import { TableSearchFilterEmptyPanel } from '@/components/ui/TableSearchFilterEmptyState'
import { RESIDENT_LIST_COLUMN_OPTIONS } from '../../constants/listColumns'
import type { ResidentListQuery, ResidentListRow } from '../../types/resident'
import type { ResidentListColumnId } from '../../types/listColumns'
import {
  isResidentListLockedColumn,
  residentListSortFieldForColumn,
} from '../../utils/residentListColumns'
import { formatApplicantIdSegment } from '../../utils/applicantIdFormat'
import {
  rentPaidStatusBadgeClass,
  rentPaidStatusLabel,
  rentalStatusLabel,
  rentalStatusTextClass,
} from '../../utils/residentListStatusStyles'
import { formatResidentListDate } from '../../utils/residentDateUtils'
import styles from '../../styles/listingTables.module.css'
import type { ResidentTableProps } from '../../types'

/**
 * Angular `canShowAccessConfirmationButton` (resident-list.component.ts:940-949):
 * show only when balance_due is null/empty/NaN or <= 0.
 */
function canShowAccessConfirmation(balanceDue?: number | string | null): boolean {
  if (balanceDue === undefined || balanceDue === null || balanceDue === '') return true
  const n = Number(balanceDue)
  if (Number.isNaN(n)) return true
  return n <= 0
}

function formatDate(value?: string, mode: 'date' | 'year' = 'date') {
  return formatResidentListDate(value, mode)
}

const COLUMN_WIDTHS: Record<ResidentListColumnId, number> = {
  applicant_id: 8,
  first_name: 10,
  unit_number: 5,
  company_name: 10,
  property_name: 11,
  sales_exec_name: 8,
  contract_start_date: 8,
  vintage: 6,
  rental_status: 8,
  rental_progress: 9,
  select: 7,
  pay_status: 12,
  access_confirmation: 10,
}

const DEFAULT_COLUMN_ID_ORDER = RESIDENT_LIST_COLUMN_OPTIONS.map((column) => column.id)

const CELL_CLASS =
  'break-words px-2.5 py-2.5 !text-center !align-middle'

function SortHeader({
  column,
  query,
  children,
  onSort,
  sortInteracted,
  className,
  align = 'center',
}: {
  column: string
  query: ResidentListQuery
  children: ReactNode
  onSort: (column: string) => void
  /** Show the active-sort arrow only after the user actually sorts (not the default orderBy on load). */
  sortInteracted: boolean
  className?: string
  align?: 'left' | 'center'
}) {
  const active = sortInteracted && query.orderBy === column
  const descending = query.sortBy === 'desc'
  return (
    <th
      scope="col"
      aria-sort={active ? (descending ? 'descending' : 'ascending') : 'none'}
      data-testid={`resident-table-sort-${column}`}
      className={cn(
        'cursor-pointer select-none',
        align === 'center' ? '!text-center' : '!text-left',
        className,
      )}
      onClick={() => onSort(column)}
    >
      <button
        type="button"
        className={cn(
          styles.sortHeaderButton,
          align === 'center' ? 'w-full justify-center' : 'justify-start',
        )}
        onClick={(event) => {
          event.stopPropagation()
          onSort(column)
        }}
      >
        <span className={styles.sortHeaderLabel}>{children}</span>
        <span
          aria-hidden="true"
          className={cn(styles.sortIcon, active && styles.sortIconActive)}
        >
          {active ? (descending ? '▼' : '▲') : <ChevronsUpDown className="h-3 w-3" />}
        </span>
      </button>
    </th>
  )
}

export function ResidentTable({
  rows,
  query,
  selectedApplicantIds,
  canCompanyView,
  canPropertyView,
  canSelect,
  canDetailView,
  busyAccessConfirmationApplicationId,
  visibleColumnIds,
  orderedColumnIds,
  horizontalScroll = false,
  sortInteracted = false,
  onSort,
  onSelectAll,
  onSelectRow,
  onAccessConfirmation,
}: ResidentTableProps) {
  const selectableRows = rows.filter((resident) => resident.rental_status === 'Active')
  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every((resident) =>
      selectedApplicantIds.includes(resident.applicant_id),
    )

  const visibleColumnSet = useMemo(
    () => new Set<ResidentListColumnId>(visibleColumnIds ?? DEFAULT_COLUMN_ID_ORDER),
    [visibleColumnIds],
  )

  const isColumnVisible = (columnId: ResidentListColumnId) =>
    isResidentListLockedColumn(columnId) || visibleColumnSet.has(columnId)

  const canRenderColumn = (columnId: ResidentListColumnId) => {
    switch (columnId) {
      case 'company_name':
        return canCompanyView
      case 'property_name':
        return canPropertyView
      case 'select':
        return canSelect
      default:
        return true
    }
  }

  const tableColumnIds = (orderedColumnIds ?? DEFAULT_COLUMN_ID_ORDER).filter(
    (columnId) => canRenderColumn(columnId) && isColumnVisible(columnId),
  )

  const hasVisibleColumns = tableColumnIds.length > 0
  const columnWeights = tableColumnIds.map((id) => COLUMN_WIDTHS[id])
  const totalColumnWeight = columnWeights.reduce((total, width) => total + width, 0) || 1

  const sh = (
    sortField: string,
    label: ReactNode,
    align: 'left' | 'center' = 'center',
  ) => (
    <SortHeader
      column={sortField}
      query={query}
      onSort={onSort}
      sortInteracted={sortInteracted}
      align={align}
    >
      {label}
    </SortHeader>
  )

  const renderColumnHeader = (columnId: ResidentListColumnId) => {
    switch (columnId) {
      case 'select':
        return (
          <th
            className="!text-center !align-bottom"
            data-testid="resident-table-select-all-cell"
          >
            <label className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap">
              Select All
              <input
                type="checkbox"
                checked={allSelected}
                onChange={(event) => onSelectAll(event.target.checked)}
                data-testid="resident-table-select-all"
                aria-label="Select all residents"
              />
            </label>
          </th>
        )
      case 'applicant_id':
        return sh('applicant_id', 'Applicant')
      case 'first_name':
        return sh('first_name', 'Resident Name')
      case 'unit_number':
        return sh('unit_number', 'Unit #')
      case 'company_name':
        return sh('company_name', 'Company')
      case 'property_name':
        return sh('property_name', 'Property')
      case 'sales_exec_name':
        return sh('sales_exec_name', 'Sales Exec')
      case 'contract_start_date':
        return sh('contract_start_date', 'Lease Start Date')
      case 'vintage':
        return sh(
          residentListSortFieldForColumn(columnId) || 'contract_start_date',
          'Vintage Year',
        )
      case 'rental_status':
        return sh('rental_status', 'Rental Status')
      case 'rental_progress':
        return sh('rental_progress', 'Rental Progress')
      case 'pay_status':
        return sh('pay_status', 'Rent Paid Status')
      case 'access_confirmation':
        return (
          <th
            className="!text-center"
            data-testid="resident-table-access-confirmation-header"
          >
            Access Confirmation
          </th>
        )
      default:
        return null
    }
  }

  const renderColumnCell = (
    columnId: ResidentListColumnId,
    resident: ResidentListRow,
    rowTestId: string,
    idSeg: string,
  ) => {
    const selected = selectedApplicantIds.includes(resident.applicant_id)
    const hideCheckbox =
      resident.rental_status === 'Eviction' &&
      resident.rental_progress === 'Completed' &&
      ['PAID', 'DENIED'].includes(String(resident.claim_status || ''))
    const isBusyAccess =
      busyAccessConfirmationApplicationId != null &&
      String(busyAccessConfirmationApplicationId) === String(resident.application_id)

    switch (columnId) {
      case 'applicant_id':
        return (
          <td
            className={CELL_CLASS}
            data-testid={`${rowTestId}-applicant`}
          >
            {canDetailView ? (
              <Link
                className="font-semibold text-blue-600 underline"
                to={`/resident/resident-detail/${idSeg}/${resident.fk_user_id}`}
                data-testid={`${rowTestId}-applicant-link`}
              >
                {idSeg}
              </Link>
            ) : (
              idSeg
            )}
          </td>
        )
      case 'first_name':
        return (
          <td
            className={CELL_CLASS}
            data-testid={`${rowTestId}-name`}
          >
            {resident.first_name} {resident.last_name}
          </td>
        )
      case 'unit_number':
        return (
          <td
            className={CELL_CLASS}
            data-testid={`${rowTestId}-unit`}
          >
            {resident.unit_number}
          </td>
        )
      case 'company_name':
        return (
          <td
            className={CELL_CLASS}
            data-testid={`${rowTestId}-company`}
          >
            {resident.company_name}
          </td>
        )
      case 'property_name':
        return (
          <td
            className={CELL_CLASS}
            data-testid={`${rowTestId}-property`}
          >
            {resident.property_name}
          </td>
        )
      case 'sales_exec_name':
        return (
          <td
            className={CELL_CLASS}
            data-testid={`${rowTestId}-sales-exec`}
          >
            {resident.sales_exec_name}
          </td>
        )
      case 'contract_start_date':
        return (
          <td
            className={cn(CELL_CLASS, 'tabular-nums')}
            data-testid={`${rowTestId}-lease-start`}
          >
            {formatDate(resident.contract_start_date)}
          </td>
        )
      case 'vintage':
        return (
          <td
            className={cn(CELL_CLASS, 'tabular-nums')}
            data-testid={`${rowTestId}-vintage-year`}
          >
            {formatDate(resident.contract_start_date, 'year')}
          </td>
        )
      case 'rental_status':
        return (
          <td
            className={CELL_CLASS}
            data-testid={`${rowTestId}-rental-status`}
          >
            <span className={rentalStatusTextClass(resident.rental_status)}>
              {rentalStatusLabel(resident.rental_status)}
            </span>
          </td>
        )
      case 'rental_progress':
        return (
          <td
            className={CELL_CLASS}
            data-testid={`${rowTestId}-rental-progress`}
          >
            {resident.rental_progress}
          </td>
        )
      case 'select':
        return (
          <td
            className={CELL_CLASS}
            data-testid={`${rowTestId}-select-cell`}
          >
            {!hideCheckbox &&
            ['Active'].includes(String(resident.rental_status || '')) ? (
              <input
                type="checkbox"
                checked={selected}
                onChange={(event) => onSelectRow(resident, event.target.checked)}
                data-testid={`${rowTestId}-select`}
                aria-label={`Select resident ${idSeg}`}
              />
            ) : null}
          </td>
        )
      case 'pay_status':
        return (
          <td
            className={cn(CELL_CLASS, 'overflow-visible')}
            data-testid={`${rowTestId}-pay-status`}
          >
            <span className={rentPaidStatusBadgeClass(resident.pay_status)}>
              {rentPaidStatusLabel(resident.pay_status)}
            </span>
          </td>
        )
      case 'access_confirmation':
        return (
          <td
            className={CELL_CLASS}
            data-testid={`${rowTestId}-access-confirmation-cell`}
          >
            {resident.application_id &&
            canShowAccessConfirmation(resident.balance_due) ? (
              <AccessConfirmationIconButton
                onClick={() =>
                  onAccessConfirmation(resident.application_id as string | number)
                }
                busy={isBusyAccess}
                title="Click to Download"
                dataTestId={`${rowTestId}-access-confirmation-button`}
                className="mx-auto"
              />
            ) : null}
          </td>
        )
      default:
        return null
    }
  }

  return (
    <div
      className={cn(styles.tableCard, 'mb-0 max-w-full rounded-none border-0 shadow-none')}
      data-testid="resident-table-wrap"
    >
      <div
        className={cn(
          horizontalScroll ? styles.tableResponsiveScroll : styles.tableResponsive,
          'max-w-full',
          horizontalScroll && 'touch-scroll',
        )}
        data-testid={
          horizontalScroll ? 'resident-table-scroll' : 'resident-table-responsive'
        }
      >
        <table
          data-testid="resident-table"
          className={cn(
            styles.listingTable,
            styles.residentListingTable,
            horizontalScroll && styles.residentListingTableScroll,
            'w-full text-sm',
            '[&_thead_th]:align-middle [&_tbody_td]:align-middle',
            '[&_thead_th]:whitespace-normal [&_tbody_td]:whitespace-normal',
            '[&_thead_th]:break-words [&_tbody_td]:break-words',
          )}
        >
          <colgroup>
            {columnWeights.map((width, index) => (
              <col
                key={`${width}-${index}`}
                style={{ width: `${(width / totalColumnWeight) * 100}%` }}
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              {hasVisibleColumns ? (
                tableColumnIds.map((columnId) => (
                  <Fragment key={columnId}>{renderColumnHeader(columnId)}</Fragment>
                ))
              ) : (
                <th className="!text-left px-3 py-3 text-sm font-semibold text-slate-600">
                  No columns selected. Use Columns to choose fields for this table.
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {hasVisibleColumns
              ? rows.map((resident) => {
                  const idSeg = formatApplicantIdSegment(resident.applicant_id)
                  const rowTestId = `resident-table-row-${idSeg}`
                  return (
                    <tr
                      key={`${resident.applicant_id}-${resident.fk_user_id}`}
                      data-testid={rowTestId}
                      className="text-[13px] leading-snug transition-colors"
                    >
                      {tableColumnIds.map((columnId) => (
                        <Fragment key={columnId}>
                          {renderColumnCell(columnId, resident, rowTestId, idSeg)}
                        </Fragment>
                      ))}
                    </tr>
                  )
                })
              : null}
          </tbody>
        </table>
      </div>
      {rows.length === 0 ? (
        <TableSearchFilterEmptyPanel
          className="m-3 border-0 shadow-none sm:m-4"
          data-testid="resident-table-empty"
        />
      ) : null}
    </div>
  )
}
